import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, fileUrl } from "@/api";
import { cld } from "@/lib/cld";
import { videoPoster, videoSources } from "@/lib/cldVideo";
import { Section, Overline } from "@/components/Primitives";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const CATS = ["all", "plats", "salle", "ambiance", "equipe", "evenements"];

// Placeholder gallery so the page is alive on first load.
const PLACEHOLDERS = [
  { id: "p1", type: "photo", url: "https://images.pexels.com/photos/8112888/pexels-photo-8112888.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200", category: "plats" },
  { id: "p2", type: "photo", url: "https://images.pexels.com/photos/16046683/pexels-photo-16046683.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200", category: "salle" },
  { id: "p3", type: "photo", url: "https://images.pexels.com/photos/880424/pexels-photo-880424.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200", category: "ambiance" },
  { id: "p4", type: "photo", url: "https://images.unsplash.com/photo-1547595628-c61a29f496f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxzb21tZWxpZXIlMjBwb3VyaW5nJTIwd2luZSUyMGRhcmt8ZW58MHx8fHwxNzgxODI5NTk0fDA&ixlib=rb-4.1.0&q=85", category: "ambiance" },
  { id: "p5", type: "photo", url: "https://images.pexels.com/photos/8112980/pexels-photo-8112980.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200", category: "plats" },
  { id: "p6", type: "photo", url: "https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxmaW5lJTIwZGluaW5nJTIwcGxhdGVkJTIwZGlzaCUyMGRhcmslMjBtb29keXxlbnwwfHx8fDE3ODE4Mjk1OTR8MA&ixlib=rb-4.1.0&q=85", category: "plats" },
];

export default function Gallery() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("all");
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  useEffect(() => {
    api.get("/gallery").then(r => {
      const dbItems = r.data || [];
      setItems(dbItems.length ? dbItems : PLACEHOLDERS);
    }).catch(() => setItems(PLACEHOLDERS));
  }, []);

  const resolveUrl = (it, preset = "card") => {
    const raw = it.url?.startsWith("http") ? it.url : (it.url?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${it.url}` : it.url);
    return cld(raw, preset);
  };
  const filtered = active === "all" ? items : items.filter(i => i.category === active);
  const lightbox = lightboxIdx >= 0 ? filtered[lightboxIdx] : null;

  const close = useCallback(() => setLightboxIdx(-1), []);
  const prev = useCallback(() => setLightboxIdx(i => i <= 0 ? filtered.length - 1 : i - 1), [filtered.length]);
  const next = useCallback(() => setLightboxIdx(i => i >= filtered.length - 1 ? 0 : i + 1), [filtered.length]);

  // Keyboard navigation
  useEffect(() => {
    if (lightboxIdx < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx, close, prev, next]);

  return (
    <div data-testid="gallery-page" className="pt-32">
      <Section testId="gallery-header">
        <Overline>{t("gallery_page.overline")}</Overline>
        <h1 className="font-serif-luxe text-5xl sm:text-6xl font-light tracking-tight text-[#F8F5EF] mt-3 mb-6">{t("gallery_page.title")}</h1>
        <p className="max-w-3xl text-[#F8F5EF]/70 leading-relaxed">{t("gallery_page.intro")}</p>

        <div className="mt-10 flex flex-wrap gap-2">
          {CATS.map(c => (
            <button
              key={c}
              data-testid={`gallery-filter-${c}`}
              onClick={() => setActive(c)}
              className={`px-5 py-2.5 text-[10px] tracking-[0.22em] uppercase border transition-all ${active === c ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-[#D4AF37]/20 text-[#F8F5EF]/70 hover:border-[#D4AF37]/50"}`}
            >
              {c === "all" ? t("gallery_page.filter_all") : t(`gallery_page.cat_${c}`)}
            </button>
          ))}
        </div>
      </Section>

      <Section testId="gallery-grid" className="!pt-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((it, idx) => (
            <button
              key={it.id || idx}
              data-testid={`gallery-item-${idx}`}
              onClick={() => setLightboxIdx(idx)}
              className="relative aspect-[4/5] overflow-hidden group bg-[#1A1A1A] border border-[#D4AF37]/15 hover:border-[#D4AF37]/40 transition-all"
            >
              {it.type === "video" ? (
                <video poster={videoPoster(it.url)} preload="none" muted playsInline className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105">
                  {videoSources(it.url).map((s, i) => <source key={i} src={s.src} media={s.media} type="video/mp4" />)}
                </video>
              ) : (
                <img loading="lazy" src={resolveUrl(it, "card")} alt={it.alt?.fr || "Le 17"} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      </Section>

      {lightbox && (
        <div data-testid="gallery-lightbox" onClick={close} className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 cursor-zoom-out">
          {/* Counter */}
          <div className="absolute top-6 left-6 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]" data-testid="lightbox-counter">
            {lightboxIdx + 1} / {filtered.length}
          </div>
          {/* Close */}
          <button data-testid="lightbox-close" onClick={(e) => { e.stopPropagation(); close(); }} className="absolute top-6 right-6 text-[#D4AF37] hover:text-[#E5C869] transition-colors p-2" aria-label="Close">
            <X size={28} />
          </button>
          {/* Prev */}
          {filtered.length > 1 && (
            <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 text-[#D4AF37] hover:text-[#E5C869] transition-colors p-3 bg-[#0A0A0A]/40 backdrop-blur-sm border border-[#D4AF37]/30 hover:border-[#D4AF37]" aria-label="Previous">
              <ChevronLeft size={28} />
            </button>
          )}
          {/* Next */}
          {filtered.length > 1 && (
            <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 text-[#D4AF37] hover:text-[#E5C869] transition-colors p-3 bg-[#0A0A0A]/40 backdrop-blur-sm border border-[#D4AF37]/30 hover:border-[#D4AF37]" aria-label="Next">
              <ChevronRight size={28} />
            </button>
          )}
          {/* Hint */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.25em] uppercase text-[#F8F5EF]/40 hidden lg:block">
            ← → · ESC
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            {lightbox.type === "video" ? (
              <video poster={videoPoster(lightbox.url)} controls autoPlay muted preload="metadata" className="max-w-[92vw] max-h-[85vh]">
                {videoSources(lightbox.url).map((s, i) => <source key={i} src={s.src} media={s.media} type="video/mp4" />)}
              </video>
            ) : (
              <img src={resolveUrl(lightbox, "hero")} alt={lightbox.alt?.fr || ""} className="max-w-[92vw] max-h-[85vh] object-contain" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
